import React, { useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';

export interface ReportExportButtonsProps {
  onExportExcel: () => Promise<void>;
  onExportPDF: () => Promise<void>;
  disabled?: boolean;
}

export const ReportExportButtons: React.FC<ReportExportButtonsProps> = ({
  onExportExcel,
  onExportPDF,
  disabled = false,
}) => {
  const { colors } = useTheme();
  const [excelLoading, setExcelLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  const handleExcel = async () => {
    if (disabled || excelLoading) return;
    setExcelLoading(true);
    try {
      await onExportExcel();
    } finally {
      setExcelLoading(false);
    }
  };

  const handlePdf = async () => {
    if (disabled || pdfLoading) return;
    setPdfLoading(true);
    try {
      await onExportPDF();
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <div className="inline-flex items-center gap-1.5">
      <button
        type="button"
        onClick={handleExcel}
        disabled={disabled || excelLoading}
        className={cn(
          'inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors',
          disabled || excelLoading
            ? 'opacity-50 cursor-not-allowed'
            : cn(colors.surfaceHover, colors.text, 'hover:bg-gray-100 dark:hover:bg-gray-700'),
          colors.border,
          'border'
        )}
        title="Export to Excel"
      >
        {excelLoading ? (
          <span className="animate-pulse">...</span>
        ) : (
          <>
            <svg className="w-4 h-4 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span>Excel</span>
          </>
        )}
      </button>
      <button
        type="button"
        onClick={handlePdf}
        disabled={disabled || pdfLoading}
        className={cn(
          'inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors',
          disabled || pdfLoading
            ? 'opacity-50 cursor-not-allowed'
            : cn(colors.surfaceHover, colors.text, 'hover:bg-gray-100 dark:hover:bg-gray-700'),
          colors.border,
          'border'
        )}
        title="Export to PDF"
      >
        {pdfLoading ? (
          <span className="animate-pulse">...</span>
        ) : (
          <>
            <svg className="w-4 h-4 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
            <span>PDF</span>
          </>
        )}
      </button>
    </div>
  );
};
